/**
 * SidebarWorkspaceHeader — the brand + workspace switcher row at the top of the
 * sidebar, plus the search and collapse affordances.
 *
 * Extracted from AppSidebar so secondary surfaces that replace the platform
 * sidebar (Teammate Code) render an identical header rather than a lookalike
 * that drifts. Everything app-scoped (nav links, CoWork tabs) stays with the
 * caller; this is only the workspace identity block.
 */

import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useWorkspaceSwitcher } from "@/hooks/use-workspace-switcher"
import { useWorkspaces } from "@/store/slices"
import { logger } from "@taqat/core/logger"
import { toast } from "sonner"
import { ChevronsUpDown, Search } from "lucide-react"
import { SidebarTrigger } from "@/components/ui/sidebar"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import logoIcon from "@/assets/images/logo-icon.png"
import { useCurrentWorkspace } from "@/store/slices/workspace-slice"

const getWorkspaceInitials = (name: string) =>
  name?.split(" ").map((w) => w.charAt(0).toUpperCase()).slice(0, 2).join("")

interface SidebarWorkspaceHeaderProps {
  /**
   * Whether the search button is shown. Surfaces without the platform's
   * command palette (Teammate Code) opt out rather than render a dead control.
   */
  showSearch?: boolean
}

export function SidebarWorkspaceHeader({ showSearch = true }: SidebarWorkspaceHeaderProps) {
  const currentWorkspace = useCurrentWorkspace()
  const workspaces = useWorkspaces()
  const { switchWorkspace, isSwitching, clearError, canSwitchTo } = useWorkspaceSwitcher()

  const handleWorkspaceSwitch = async (workspaceId: string) => {
    clearError()
    try {
      await switchWorkspace(workspaceId)
      toast.success('Workspace switched successfully')
    } catch (error) {
      logger.error(`Failed to switch workspace: ${error instanceof Error ? error.message : String(error)}`)
      toast.error('Failed to switch workspace', {
        description: error instanceof Error ? error.message : 'Failed to switch workspace',
      })
    }
  }

  return (
    <div className="flex items-center justify-between group-data-[collapsible=icon]:justify-center">
      <DropdownMenu>
        <DropdownMenuTrigger
          disabled={isSwitching}
          className="flex items-center gap-3 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring group/ws group-data-[collapsible=icon]:gap-0"
          aria-label="Switch workspace"
        >
          <div className="size-10 shrink-0 flex items-center justify-center">
            <img
              src={logoIcon}
              alt="TeamMate"
              className="size-9 object-contain"
              onError={(e) => {
                const target = e.target as HTMLImageElement
                const wrapper = target.parentElement
                if (wrapper) {
                  wrapper.innerHTML =
                    '<span class="text-sm font-bold text-primary flex items-center justify-center size-10">TM</span>'
                }
              }}
            />
          </div>
          <div className="flex flex-col text-left group-data-[collapsible=icon]:hidden">
            <span className="type-sidebar-title">
              <span className="text-foreground">Team</span>
              <span className="text-primary">Mate</span>
            </span>
            <span className="type-sidebar-meta max-w-[140px] truncate text-muted-foreground">
              {isSwitching ? 'Switching…' : currentWorkspace?.name ?? 'Workspace'}
            </span>
          </div>
          <ChevronsUpDown className="size-3.5 text-muted-foreground/60 opacity-0 group-hover/ws:opacity-100 transition-opacity group-data-[collapsible=icon]:hidden" />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-72 rounded-lg max-h-80 overflow-y-auto p-1.5"
          align="start"
          side="bottom"
          sideOffset={6}
        >
          <DropdownMenuLabel className="type-label text-muted-foreground px-2 pt-1 pb-1.5 text-xs uppercase tracking-wider">
            Workspaces
          </DropdownMenuLabel>
          {workspaces.map((workspace) => {
            const isCurrent = currentWorkspace?.id === workspace.id
            return (
              <DropdownMenuItem
                key={workspace.id}
                onClick={() => handleWorkspaceSwitch(workspace.id)}
                className={cn(
                  "gap-3 px-2 py-2 rounded-md cursor-pointer transition-colors",
                  isCurrent &&
                    "bg-gradient-to-r from-primary/15 via-primary/10 to-primary/5 ring-1 ring-inset ring-primary/20 data-[disabled]:opacity-100"
                )}
                disabled={isSwitching || !canSwitchTo(workspace.id)}
              >
                <Avatar
                  className={cn(
                    "size-8 shrink-0 rounded-md",
                    isCurrent ? "bg-primary/15 ring-1 ring-primary/30" : "bg-muted"
                  )}
                >
                  <AvatarFallback
                    className={cn(
                      "rounded-md text-[11px] font-semibold",
                      isCurrent ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
                    )}
                  >
                    {getWorkspaceInitials(workspace.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div
                    className={cn(
                      "type-sidebar-title truncate text-sm font-medium",
                      isCurrent && "text-primary font-semibold"
                    )}
                  >
                    {workspace.name}
                  </div>
                  {workspace.description && (
                    <div
                      className={cn(
                        "type-sidebar-meta truncate text-xs",
                        isCurrent ? "text-primary/70" : "text-muted-foreground"
                      )}
                    >
                      {workspace.description}
                    </div>
                  )}
                </div>
              </DropdownMenuItem>
            )
          })}
        </DropdownMenuContent>
      </DropdownMenu>
      <div className="flex items-center gap-1 group-data-[collapsible=icon]:hidden">
        {showSearch && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={() => window.dispatchEvent(new CustomEvent('taqat:open-search'))}
                aria-label="Search"
              >
                <Search className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Search (⌘K)</TooltipContent>
          </Tooltip>
        )}
        <SidebarTrigger className="size-8" />
      </div>
    </div>
  )
}

SidebarWorkspaceHeader.displayName = "SidebarWorkspaceHeader"
